import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { msalInstance, loginRequest, armRequest, isAzureConfigured, prefersRedirectAuth, isPopupUnusableError, isInteractionInProgressError, clearStaleInteraction } from "@/authConfig";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { PanelLeftOpen } from "lucide-react";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import PackagesPage from "@/pages/PackagesPage";
import DeploymentsPage from "@/pages/DeploymentsPage";
import SettingsPage from "@/pages/SettingsPage";
import AppDetailsPage from "@/pages/AppDetailsPage";
import DiscoverPage from "@/pages/DiscoverPage";
import AppGovernancePage from "@/pages/AppGovernancePage";
import CVEManagementPage from "@/pages/CVEManagementPage";
import MainDashboard from "@/pages/MainDashboard";
import AppsDashboard from "@/pages/AppsDashboard";
import FailuresPage from "@/pages/FailuresPage";
import TenantOnboardingPage from "@/pages/TenantOnboardingPage";
import TenantSuspendedPage from "@/pages/TenantSuspendedPage";
import { OSPatchDashboard, OSUpdatesPage, DeploymentGroupsPage, ReportsPage, RemediationPage } from "@/pages/OSPatchPages";
import Sidebar from "@/components/Sidebar";
import { PermissionProtectedRoute } from "@/components/PermissionProtectedRoute";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demoMode, setDemoMode] = useState(!isAzureConfigured());
  const [tenantStatus, setTenantStatus] = useState(null);
  const [checkingTenant, setCheckingTenant] = useState(false);
  // The tenant every request is scoped to. localStorage stays the source
  // of truth (the axios interceptor and several pages read it directly);
  // mirroring it in state lets PermissionsContext re-fetch when the login
  // flow picks a tenant, without a full page reload.
  const [selectedTenantId, setSelectedTenantId] = useState(
    () => localStorage.getItem("alice_selected_tenant_id")
  );
  const selectTenant = useCallback((tenantId) => {
    if (tenantId) {
      localStorage.setItem("alice_selected_tenant_id", tenantId);
    } else {
      localStorage.removeItem("alice_selected_tenant_id");
    }
    setSelectedTenantId(tenantId || null);
  }, []);

  // Check tenant status when authenticated (non-demo mode)
  const checkTenantStatus = useCallback(async (token) => {
    if (demoMode) return null;
    
    try {
      setCheckingTenant(true);
      // If the user has actively selected a tenant they're a member of
      // (e.g. a multi-tenant viewer whose own home tenant is not
      // onboarded), check status against *that* tenant — otherwise the
      // status check always uses the JWT `tid` (home tenant) and bounces
      // them back to /onboarding on every refresh, trapping them on a
      // stale Discover bundle. This `fetch` call does NOT go through
      // the axios interceptor, so the header has to be set explicitly.
      const selectedTenantId = localStorage.getItem("alice_selected_tenant_id");
      const requestHeaders = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };
      if (selectedTenantId) {
        requestHeaders["X-ALICE-Tenant-ID"] = selectedTenantId;
      }

      const response = await fetch(`${API}/tenant/status`, {
        headers: requestHeaders,
      });
      
      // Check if response is OK before parsing JSON
      if (!response.ok) {
        // Handle error responses gracefully
        const errorText = await response.text();
        console.warn(`Tenant status check failed (${response.status}): ${errorText}`);
        // 2026-07-30 incident hardening: a 5xx / gateway error means the
        // BACKEND is unhealthy — it says nothing about tenant
        // registration. Returning {registered:false} here used to bounce
        // every user of every tenant to /onboarding during a bad deploy.
        // null = "could not check — stay where you are".
        if (response.status >= 500) {
          return null;
        }
        // Genuine 4xx (bad/expired token etc.) keeps the old behaviour.
        return { registered: false, status: "error" };
      }
      
      const data = await response.json();
      setTenantStatus(data);
      return data;
    } catch (error) {
      // Network-level failure (backend down / DNS / CORS) — same as 5xx:
      // do NOT treat as "tenant not registered".
      console.error("Failed to check tenant status:", error);
      return null;
    } finally {
      setCheckingTenant(false);
    }
  }, [demoMode]);

  // Tenant id already validated (membership + MFA) in this page session,
  // so moving between pages does not repeat the discovery calls.
  const validatedTenantRef = useRef(null);

  // Decide which tenant this session uses. Runs after sign-in, BEFORE any
  // tenant status check, so access is decided by the user's invitations
  // and not by whether their own Entra tenant happens to be registered.
  // An external user of an offline SCCM tenant never has a registered
  // home tenant: the SCCM tenant id is a synthetic UUID that matches
  // nobody's JWT `tid`.
  //
  // Precedence:
  //   1. the stored selection, if the user can still use it
  //   2. the user's own (home) tenant, if registered and active
  //   3. the first tenant they have been invited to
  // Onboarding is not a fallback here. It is only for users with no
  // usable tenant at all (see ProtectedRoute) or for anyone who visits
  // /onboarding deliberately.
  //
  // Returns { tenant, blocked, unavailable }:
  //   tenant       the selection made (null when there is none)
  //   blocked      invitations that exist but could not be used, with the
  //                server's reason (typically "MFA required")
  //   unavailable  the tenant list itself could not be fetched (backend
  //                unhealthy / auth hiccup). Callers must not read that as
  //                "no tenants" and push the user into onboarding.
  const resolveSessionTenant = useCallback(async (token) => {
    const result = { tenant: null, blocked: [], unavailable: false };
    if (demoMode || !token) return result;

    const storedId = localStorage.getItem("alice_selected_tenant_id");
    if (storedId && validatedTenantRef.current === storedId) {
      result.tenant = { tenant_id: storedId };
      return result;
    }

    const fetchTenants = () =>
      axios.get(`${API}/users/accessible-tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    let tenants = [];
    try {
      let response;
      try {
        response = await fetchTenants();
      } catch (firstError) {
        // One retry. A first sign-in lands here straight after the consent
        // popup with cold caches on both sides; a single transient failure
        // must not decide that the user has no tenant.
        const status = firstError.response?.status;
        if (status && status < 500) throw firstError;
        await new Promise((resolve) => setTimeout(resolve, 1500));
        response = await fetchTenants();
      }
      tenants = response.data?.tenants || [];
    } catch (error) {
      console.error("Failed to load accessible tenants:", error);
      result.unavailable = true;
      return result;
    }

    // Only tenants a user can actually land on. `tenant_status` is absent
    // on an older backend; the status check after selection covers that.
    const usable = tenants.filter(
      (t) => !t.tenant_status || t.tenant_status === "active"
    );
    const ordered = [
      ...usable.filter((t) => t.tenant_id === storedId),
      ...usable.filter((t) => t.tenant_id !== storedId && t.is_home_tenant),
      ...usable.filter((t) => t.tenant_id !== storedId && !t.is_home_tenant),
    ];

    for (const candidate of ordered) {
      try {
        // Same server-side check the manual switcher uses (membership +
        // MFA for external access), so auto-selection can't grant more
        // than a deliberate switch would.
        await axios.post(
          `${API}/users/validate-tenant-access?tenant_id=${encodeURIComponent(candidate.tenant_id)}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        selectTenant(candidate.tenant_id);
        validatedTenantRef.current = candidate.tenant_id;
        result.tenant = candidate;
        console.log("[Auth] Session tenant:", candidate.display_name, candidate.is_home_tenant ? "(home)" : "(invited)");
        return result;
      } catch (error) {
        const reason = error.response?.data?.detail || "Access could not be verified";
        console.warn(`[Auth] Cannot use tenant ${candidate.display_name}: ${reason}`);
        result.blocked.push({
          tenant_id: candidate.tenant_id,
          display_name: candidate.display_name,
          reason,
        });
      }
    }
    return result;
  }, [demoMode, selectTenant]);

  useEffect(() => {
    const initAuth = async () => {
      if (demoMode) {
        // Demo mode - use mock user
        setUser({
          id: "demo-user",
          email: "admin@contoso.com",
          name: "Demo Admin",
          role: "admin",
        });
        setLoading(false);
        return;
      }

      if (isAuthenticated && accounts.length > 0) {
        try {
          const account = accounts[0];
          setUser({
            id: account.localAccountId,
            email: account.username,
            name: account.name || account.username,
            role: "admin",
            tenantId: account.tenantId,
          });
        } catch (error) {
          console.error("Auth error:", error);
        }
      }
      setLoading(false);
    };

    if (inProgress === InteractionStatus.None) {
      initAuth();
    }
  }, [isAuthenticated, accounts, inProgress, demoMode]);

  const login = useCallback(async () => {
    if (demoMode) {
      setUser({
        id: "demo-user",
        email: "admin@contoso.com",
        name: "Demo Admin",
        role: "admin",
      });
      return;
    }

    // Surface MSAL's error code: "Login failed" alone hid the difference
    // between a blocked popup, a stuck tab and a genuine auth failure.
    const reportFailure = (error) => {
      const code = error?.errorCode || error?.name || "unknown_error";
      toast.error(`Login failed (${code})`);
    };

    // Full-page redirect. If this tab is stuck with a stale "interaction
    // in progress" flag from an earlier attempt, clear it and try once more.
    const redirectLogin = async () => {
      try {
        await instance.loginRedirect(loginRequest);
      } catch (error) {
        if (!isInteractionInProgressError(error)) throw error;
        console.warn("Stale MSAL interaction flag found, clearing and retrying redirect sign-in");
        clearStaleInteraction();
        await instance.loginRedirect(loginRequest);
      }
    };

    // Mobile browsers block the popup; use a full-page redirect there.
    // MsalProvider processes the response when the browser comes back.
    if (prefersRedirectAuth()) {
      try {
        await redirectLogin();
      } catch (error) {
        console.error("Login error (redirect):", error);
        reportFailure(error);
      }
      return;
    }

    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      if (isPopupUnusableError(error) || isInteractionInProgressError(error)) {
        // Popup blocked (or tab stuck) on a desktop browser too: fall back
        // to redirect rather than reporting a failure the user cannot act on.
        console.warn("Sign-in popup unavailable, falling back to redirect:", error.errorCode);
        try {
          if (isInteractionInProgressError(error)) clearStaleInteraction();
          await redirectLogin();
          return;
        } catch (redirectError) {
          console.error("Login error (redirect fallback):", redirectError);
          reportFailure(redirectError);
          return;
        }
      }
      console.error("Login error:", error);
      reportFailure(error);
    }
  }, [instance, demoMode]);

  const logout = useCallback(async () => {
    if (demoMode) {
      setUser(null);
      return;
    }

    try {
      if (prefersRedirectAuth()) {
        await instance.logoutRedirect();
        return;
      }
      await instance.logoutPopup();
      setUser(null);
    } catch (error) {
      if (isPopupUnusableError(error)) {
        await instance.logoutRedirect();
        return;
      }
      console.error("Logout error:", error);
    }
  }, [instance, demoMode]);

  const getAccessToken = useCallback(async () => {
    if (demoMode) {
      // Return null for demo mode - backend will handle
      return null;
    }

    if (!isAuthenticated || accounts.length === 0) {
      throw new Error("Not authenticated");
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      console.error("Token acquisition error:", error);
      // Try interactive if silent fails. On mobile the popup is blocked, so
      // redirect instead; the page reloads with the token on return.
      if (prefersRedirectAuth()) {
        await instance.acquireTokenRedirect({ ...loginRequest, account: accounts[0] });
        throw error;
      }
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        return response.accessToken;
      } catch (popupError) {
        if (isPopupUnusableError(popupError)) {
          await instance.acquireTokenRedirect({ ...loginRequest, account: accounts[0] });
        }
        console.error("Interactive token acquisition failed:", popupError);
        throw popupError;
      }
    }
  }, [instance, accounts, isAuthenticated, demoMode]);

  // Acquire an Azure Resource Manager (ARM) token for the signed-in admin.
  // Used during onboarding to assign 'Storage Blob Data Contributor' to
  // ALICE's service principal without making the admin run a PowerShell
  // script. The admin must consent to the ARM scope on first use; this is
  // a benign, MS-managed scope.
  const getArmToken = useCallback(async () => {
    if (demoMode) return null;
    if (!isAuthenticated || accounts.length === 0) {
      throw new Error("Not authenticated");
    }
    try {
      const response = await instance.acquireTokenSilent({
        ...armRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      console.warn("Silent ARM token failed, falling back to popup:", error);
      const response = await instance.acquireTokenPopup(armRequest);
      return response.accessToken;
    }
  }, [instance, accounts, isAuthenticated, demoMode]);

  // Get currently selected tenant ID from localStorage
  const getSelectedTenantId = useCallback(() => {
    return localStorage.getItem("alice_selected_tenant_id");
  }, []);

  // Set up axios interceptor to add tenant header, and the sign-in token on OS
  // Patch calls.
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(async (config) => {
      const selectedTenantId = getSelectedTenantId();
      console.log("[Axios Interceptor] Request URL:", config.url);
      console.log("[Axios Interceptor] Selected Tenant ID from localStorage:", selectedTenantId);
      console.log("[Axios Interceptor] Header BEFORE setting:", config.headers["X-ALICE-Tenant-ID"]);
      if (selectedTenantId) {
        config.headers["X-ALICE-Tenant-ID"] = selectedTenantId;
        console.log("[Axios Interceptor] Header AFTER setting:", config.headers["X-ALICE-Tenant-ID"]);
      } else {
        console.warn("[Axios Interceptor] No selected tenant ID found in localStorage!");
      }
      // Attach the bearer token to EVERY call to ALICE's own API that doesn't
      // already carry one. About a hundred calls across the OS Patch pages,
      // Dashboard, Discover, Settings and Deployments use plain axios with no
      // token, relying on the tenant header above. Since 20 Oct 2026 the
      // backend middleware refuses any non-exempt request without a bearer
      // token (security incident: a header-only request naming a tenant's ID
      // was given that tenant's data), so the token has to go on all of them.
      // Only ALICE's API gets it -- never a third-party URL. A failure to get a
      // token must not stop the request: the backend then answers 401.
      if (
        !config.headers["Authorization"]
        && typeof config.url === "string"
        && config.url.startsWith(`${API}/`)
      ) {
        try {
          const token = await getAccessToken();
          if (token) config.headers["Authorization"] = `Bearer ${token}`;
        } catch (e) {
          console.warn("[Axios Interceptor] Could not attach a token to an API call:", e);
        }
      }
      return config;
    });

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [getSelectedTenantId, getAccessToken]);

  const value = {
    user,
    isAuthenticated: demoMode ? !!user : isAuthenticated,
    loading,
    login,
    logout,
    getAccessToken,
    getArmToken,
    getSelectedTenantId,
    selectedTenantId,
    selectTenant,
    resolveSessionTenant,
    demoMode,
    setDemoMode,
    tenantStatus,
    checkTenantStatus,
    checkingTenant,
  };

  // Hold the whole app while MSAL starts up or processes a redirect
  // response. The sign-in code comes back in the URL hash at "/", and the
  // router's immediate redirect to /dashboard stripped it before MSAL read
  // it. A redirect sign-in (mobile) then never completed and left the
  // tab's "interaction in progress" flag set, so every later tap failed
  // with interaction_in_progress ("Login failed"). `loading` stays true
  // until MSAL reports no interaction in progress.
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen relative z-[1]">
        <div className="text-center">
          <div className="spinner mb-4" />
          <p className="text-white/60">Signing you in...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route with Tenant Check
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, demoMode, getAccessToken, tenantStatus, checkTenantStatus, resolveSessionTenant } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tenantChecked, setTenantChecked] = useState(false);
  const [checking, setChecking] = useState(false);

  // Onboarding and the suspended page must render without a usable tenant.
  const isTenantExemptPage = location.pathname === "/onboarding" || location.pathname === "/suspended";
  const needsTenantCheck = !demoMode && isAuthenticated && !isTenantExemptPage;

  useEffect(() => {
    const verifyTenant = async () => {
      // Skip tenant check for demo mode or if already on onboarding page or suspended page
      if (demoMode || isTenantExemptPage) {
        setTenantChecked(true);
        return;
      }

      // Skip if not authenticated yet
      if (!isAuthenticated) {
        return;
      }

      // Check tenant status
      if (!tenantChecked && !checking) {
        setChecking(true);
        try {
          const token = await getAccessToken();

          // Step 1: which tenant does this user get? Decided from their
          // invitations first, so an invited user lands on that tenant
          // whether or not their own Entra tenant is registered.
          const resolved = await resolveSessionTenant(token);

          if (!resolved.tenant && !resolved.unavailable) {
            // No usable tenant anywhere. This is the only default route
            // into onboarding; anyone else reaches it via /onboarding.
            console.log("No usable tenant for this user, redirecting to onboarding", resolved.blocked);
            navigate("/onboarding", { replace: true, state: { blockedTenants: resolved.blocked } });
            return;
          }

          // Step 2: confirm the selected tenant's lifecycle status (the
          // header is read from localStorage). If the tenant list was
          // unavailable this falls back to the JWT home tenant.
          const status = await checkTenantStatus(token);

          if (status && status.status === "suspended") {
            console.log("Tenant is suspended, redirecting to suspended page", status);
            navigate("/suspended", { replace: true });
          } else if (status && (!status.registered || status.status !== "active")) {
            console.log("Tenant not registered/active, redirecting to onboarding", status);
            navigate("/onboarding", { replace: true, state: { blockedTenants: resolved.blocked } });
          }
          // status === null: backend unreachable. A validated selection
          // stands; do not bounce the user anywhere.
        } catch (error) {
          console.error("Error checking tenant:", error);
        } finally {
          setTenantChecked(true);
          setChecking(false);
        }
      }
    };

    verifyTenant();
  }, [isAuthenticated, demoMode, isTenantExemptPage, tenantChecked, checking, getAccessToken, checkTenantStatus, resolveSessionTenant, navigate]);

  // Hold the page until the tenant is resolved. Rendering children for the
  // first frame used to mount the sidebar (and its organization list
  // request) against a tenant that was about to be replaced.
  if (loading || (needsTenantCheck && !tenantChecked)) {
    return (
      <div className="flex items-center justify-center min-h-screen relative z-[1]">
        <div className="text-center">
          <div className="spinner mb-4" />
          <p className="text-white/60">Verifying tenant...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Main App Layout with Sidebar
const AppLayout = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  const isOnboardingPage = location.pathname === "/onboarding";
  const isSuspendedPage = location.pathname === "/suspended";
  // The sidebar can be hidden to give the page the full width (wide tables, the deployment wizards),
  // and brought back from a small button pinned top-left. The choice is kept per browser.
  const [sidebarHidden, setSidebarHidden] = useState(() => {
    try { return localStorage.getItem("alice_sidebar_hidden") === "1"; } catch (e) { return false; }
  });
  const toggleSidebar = useCallback(() => {
    setSidebarHidden((hidden) => {
      try { localStorage.setItem("alice_sidebar_hidden", hidden ? "0" : "1"); } catch (e) { /* private mode */ }
      return !hidden;
    });
  }, []);

  // Login page, onboarding page, and suspended page don't have sidebar
  if (isLoginPage || isOnboardingPage || isSuspendedPage || !isAuthenticated) {
    // Sign-in and tenant-suspended are "gate" screens: they keep today's look in both Light and
    // Dark (Jay, October 2026). styles/theme.css scopes every theme rule away from data-zone="gate".
    const isGate = isLoginPage || isSuspendedPage || !isAuthenticated;
    return (
      <div className="app-container" data-zone={isGate ? "gate" : undefined}>
        {children}
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen relative">
      {sidebarHidden ? (
        <button
          type="button"
          onClick={toggleSidebar}
          className="fixed top-3 left-3 z-30 h-9 w-9 flex items-center justify-center rounded-lg bg-white/15 border border-white/25 text-white backdrop-blur hover:bg-white/25 transition-colors"
          title="Show sidebar"
          aria-label="Show sidebar"
          data-testid="sidebar-show"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
      ) : (
        <Sidebar onHide={toggleSidebar} />
      )}
      <main className={`flex-1 overflow-auto relative z-[1] ${sidebarHidden ? "pl-12" : ""}`}>
        {children}
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Tenant Onboarding (for new tenants) */}
      <Route path="/onboarding" element={<ProtectedRoute><TenantOnboardingPage /></ProtectedRoute>} />
      {/* Tenant Suspended (for disabled tenants) */}
      <Route path="/suspended" element={<ProtectedRoute><TenantSuspendedPage /></ProtectedRoute>} />
      {/* Main Dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute><PermissionProtectedRoute><MainDashboard /></PermissionProtectedRoute></ProtectedRoute>} />
      {/* Apps Management - requires view_apps permission */}
      <Route path="/apps/dashboard" element={<ProtectedRoute><PermissionProtectedRoute><AppsDashboard /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/failures" element={<ProtectedRoute><PermissionProtectedRoute><FailuresPage /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/discover" element={<ProtectedRoute><PermissionProtectedRoute><DiscoverPage /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/packages" element={<ProtectedRoute><PermissionProtectedRoute><PackagesPage /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/deployments" element={<ProtectedRoute><PermissionProtectedRoute><DeploymentsPage /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/catalog" element={<ProtectedRoute><PermissionProtectedRoute><DashboardPage /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/apps/:appId" element={<ProtectedRoute><PermissionProtectedRoute><AppDetailsPage /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/discover/governance/:familyName" element={<ProtectedRoute><PermissionProtectedRoute><AppGovernancePage /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/cve" element={<ProtectedRoute><PermissionProtectedRoute><CVEManagementPage /></PermissionProtectedRoute></ProtectedRoute>} />
      {/* OS Patch Management - requires view_os_patches permission */}
      <Route path="/os-patch/dashboard" element={<ProtectedRoute><PermissionProtectedRoute><OSPatchDashboard /></PermissionProtectedRoute></ProtectedRoute>} />
      {/* October 2026: the Devices page merged into Discover > Devices. Kept as a
          redirect so bookmarks and old links still land on the device list. */}
      <Route path="/os-patch/devices" element={<Navigate to="/os-patch/discover?tab=devices" replace />} />
      <Route path="/os-patch/updates" element={<ProtectedRoute><PermissionProtectedRoute><OSUpdatesPage /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/os-patch/groups" element={<ProtectedRoute><PermissionProtectedRoute><DeploymentGroupsPage /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/os-patch/discover" element={<ProtectedRoute><PermissionProtectedRoute><ReportsPage /></PermissionProtectedRoute></ProtectedRoute>} />
      {/* Legacy alias — keep /os-patch/reports working for existing
          bookmarks while the rename to "Discover" rolls out. */}
      <Route path="/os-patch/reports" element={<ProtectedRoute><PermissionProtectedRoute><ReportsPage /></PermissionProtectedRoute></ProtectedRoute>} />
      <Route path="/os-patch/remediation" element={<ProtectedRoute><PermissionProtectedRoute><RemediationPage /></PermissionProtectedRoute></ProtectedRoute>} />
      {/* Settings */}
      <Route path="/settings" element={<ProtectedRoute><PermissionProtectedRoute><SettingsPage /></PermissionProtectedRoute></ProtectedRoute>} />
      {/* Default redirects */}
      {/* /dashboard is itself protected; wrapping the redirect too would run the tenant check twice on landing */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

import { PermissionsProvider } from "@/contexts/PermissionsContext";

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <AuthProvider>
          <PermissionsProvider>
            <AppLayout>
              <AppRoutes />
            </AppLayout>
          </PermissionsProvider>
        </AuthProvider>
      </BrowserRouter>
    </MsalProvider>
  );
}

export default App;
